import axios, { AxiosError } from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Team sent as `X-Team-Id` for this request.
     * `null` forces a personal request and ignores the stored team.
     * A string uses that team. Omit to use the stored team.
     */
    teamScope?: string | null;
  }
}
import {
  API_BASE_URL,
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
} from '../config/api';
import { MA_ACTIVE_TEAM_ID_KEY } from '../constants/teamStorage';
import {
  APP_VIEW_ONLY_MESSAGE,
  isViewOnlyAccessMessage,
} from '../utils/writeAccess';
import { getAuthWriteGeneration, isStaleAuthWrite } from './authSessionGuard';

const timeout = 900000; // 15 minutes

/** Verbose HTTP trace (request line, response status, token refresh steps) — dev only. */
const isHttpTraceEnabled = import.meta.env.DEV;

let activeRequests = 0;
const FAMILY_ACTIVE_PROFILE_STORAGE_PREFIX = 'ma_family_active_profile_v1_';
const FAMILY_ACTIVE_PROFILE_ROLE_STORAGE_PREFIX =
  'ma_family_active_profile_role_v1_';
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const WRITE_BLOCK_EXEMPT_PATHS = ['/auth/switch-profile'];
// Reserved for endpoints that must always be resolved in the raw-auth-user
// context (ignoring the active family profile). Intentionally empty: the
// backend acting-profile middleware (auth.middleware.js) is the single source
// of truth for scoping, and /family/members must honour it so a profile
// switched into another account does not expose profiles that were never
// shared with that account.
const ACTING_PROFILE_EXEMPT_PATHS: string[] = [];

const toggleLoadingClass = (_isLoading: boolean) => {
  // Removed to prevent disruptive wait cursor during background saves
};

const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true', // Skip ngrok warning page
  },
});

function getStoredUserId(): string | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string | number };
    if (parsed?.id == null) return null;
    const id = String(parsed.id).trim();
    return id || null;
  } catch {
    return null;
  }
}

function getActingFamilyProfileId(): string | null {
  const userId = getStoredUserId();
  if (!userId) return null;
  try {
    const key = `${FAMILY_ACTIVE_PROFILE_STORAGE_PREFIX}${userId}`;
    const value = localStorage.getItem(key);
    const normalized = value?.trim();
    return normalized || null;
  } catch {
    return null;
  }
}

function getActingFamilyProfileRole(): string | null {
  const userId = getStoredUserId();
  if (!userId) return null;
  try {
    const key = `${FAMILY_ACTIVE_PROFILE_ROLE_STORAGE_PREFIX}${userId}`;
    const value = localStorage.getItem(key);
    const normalized = value?.trim().toLowerCase();
    return normalized || null;
  } catch {
    return null;
  }
}

function isWriteMethod(config: InternalAxiosRequestConfig): boolean {
  const method = String(config.method || 'get').toLowerCase();
  return WRITE_METHODS.has(method);
}

function isWriteBlockExempt(config: InternalAxiosRequestConfig): boolean {
  const url = String(config.url || '');
  return WRITE_BLOCK_EXEMPT_PATHS.some((p) => url.startsWith(p));
}

function isActingProfileExempt(config: InternalAxiosRequestConfig): boolean {
  const url = String(config.url || '');
  return ACTING_PROFILE_EXEMPT_PATHS.some((p) => url.startsWith(p));
}

/** If the caller set `X-Acting-Profile-Id` on the request, do not override from localStorage. */
function getPinnedActingProfileId(
  config: InternalAxiosRequestConfig,
): string | null {
  const h = config.headers;
  if (!h) return null;
  const anyH = h as Record<string, unknown> & {
    get?: (key: string) => string | undefined;
  };
  const raw =
    (typeof anyH.get === 'function'
      ? (anyH.get('X-Acting-Profile-Id') ?? anyH.get('x-acting-profile-id'))
      : null) ??
    anyH['X-Acting-Profile-Id'] ??
    anyH['x-acting-profile-id'];
  if (raw == null) return null;
  const s = String(raw).trim();
  return s || null;
}

// Request interceptor - Add Authorization header
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    activeRequests++;
    toggleLoadingClass(true);

    const token = localStorage.getItem(TOKEN_KEY);
    const url = config.url || '';
    if (isHttpTraceEnabled) {
      console.log('📤 [API REQUEST]', url, { hasToken: !!token });
    }
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.headers) {
      const explicitScope = (config as AxiosRequestConfig).teamScope;
      if (explicitScope === null) {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('X-Team-Id');
        }
      } else {
        const activeTeamId =
          typeof explicitScope === 'string' && explicitScope.trim()
            ? explicitScope.trim()
            : localStorage.getItem(MA_ACTIVE_TEAM_ID_KEY);
        if (activeTeamId) {
          config.headers['X-Team-Id'] = activeTeamId;
        }
      }
    }

    if (!isActingProfileExempt(config)) {
      if (!getPinnedActingProfileId(config)) {
        const actingProfileId = getActingFamilyProfileId();
        if (actingProfileId && config.headers) {
          (config.headers as Record<string, string>)['X-Acting-Profile-Id'] =
            actingProfileId;
        }
      }
    }

    const actingRole = getActingFamilyProfileRole();
    if (
      actingRole === 'viewer' &&
      isWriteMethod(config) &&
      !isWriteBlockExempt(config)
    ) {
      // Do not toast here — callers / UI guards show a single message.
      return Promise.reject(
        new AxiosError(APP_VIEW_ONLY_MESSAGE, 'ERR_FORBIDDEN', config),
      );
    }

    // Remove Content-Type for FormData to let browser set it with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    activeRequests--;
    if (activeRequests <= 0) {
      activeRequests = 0;
      toggleLoadingClass(false);
    }
    return Promise.reject(error);
  },
);

// Response interceptor - Handle 401 and errors
let isRefreshing = false;
/** After a 429 on refresh, skip hammering the refresh endpoint until this time (ms). */
let refreshCooldownUntil = 0;
/** Throttle repeated team-membership stale notifications on burst 403s. */
let lastTeamMembershipStaleAt = 0;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

http.interceptors.response.use(
  (response) => {
    activeRequests--;
    if (activeRequests <= 0) {
      activeRequests = 0;
      toggleLoadingClass(false);
    }
    // Check if response is HTML (ngrok warning page or error page)
    const contentType = String(response.headers['content-type'] || '');
    if (contentType.includes('text/html')) {
      const error = new Error(
        'Server returned HTML instead of JSON. This might be an ngrok warning page. Please check your API endpoint configuration.',
      );
      console.error('API Error:', error.message);
      throw error;
    }
    return response;
  },
  async (
    error: AxiosError<{
      message?: string;
      error?: string;
      errors?: Record<string, unknown> | string[];
    }>,
  ) => {
    activeRequests--;
    if (activeRequests <= 0) {
      activeRequests = 0;
      toggleLoadingClass(false);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const requestUrl = originalRequest?.url || '';
    const status = error.response?.status;

    if (isHttpTraceEnabled) {
      console.log('📥 [API RESPONSE]', requestUrl, {
        status,
        hasResponse: !!error.response,
      });
    }

    if (status === 403) {
      const headerTeamId =
        (originalRequest?.headers?.['X-Team-Id'] as string | undefined) || '';
      const hasTeamHeader = String(headerTeamId).trim().length > 0;
      const isTeamRoute = requestUrl.includes('/team/');
      const isTeamRecipeRoute =
        requestUrl.includes('/recipe') &&
        (requestUrl.includes('/team/workspace') || hasTeamHeader);

      if (hasTeamHeader || isTeamRoute || isTeamRecipeRoute) {
        const now = Date.now();
        if (now - lastTeamMembershipStaleAt > 1200) {
          lastTeamMembershipStaleAt = now;
          window.dispatchEvent(
            new CustomEvent('meraayurved:team-membership-stale', {
              detail: { status, url: requestUrl },
            }),
          );
        }
      }

      const apiMessage =
        error.response?.data?.message || error.response?.data?.error || '';
      const actingRole = getActingFamilyProfileRole();
      const isViewerWrite =
        !!originalRequest &&
        actingRole === 'viewer' &&
        isWriteMethod(originalRequest) &&
        !isWriteBlockExempt(originalRequest);
      if (isViewerWrite || isViewOnlyAccessMessage(apiMessage)) {
        // Normalize only — toast once from UI / notifyViewOnlyAccess callers.
        error.message = APP_VIEW_ONLY_MESSAGE;
        if (error.response?.data && typeof error.response.data === 'object') {
          (error.response.data as { message?: string }).message =
            APP_VIEW_ONLY_MESSAGE;
        }
      }
    }

    // Check if error response is HTML
    if (
      String(error.response?.headers['content-type'] || '').includes(
        'text/html',
      )
    ) {
      const htmlError = new Error(
        'Server returned HTML instead of JSON. Please check your API endpoint configuration.',
      );
      console.error('❌ [API ERROR] HTML response:', htmlError.message);
      return Promise.reject(htmlError);
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      // Don't retry if it's the refresh request itself
      if (requestUrl.includes('/auth/refresh-token')) {
        if (isHttpTraceEnabled) {
          console.log('Refresh token expired or invalid, logging out');
        }
        // Fall through to logout logic
      } else {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return http(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (refreshToken) {
          if (Date.now() < refreshCooldownUntil) {
            console.warn(
              '⏳ Token refresh skipped (cooldown after rate limit)',
            );
            return Promise.reject(error);
          }

          originalRequest._retry = true;
          isRefreshing = true;
          const generationAtStart = getAuthWriteGeneration();

          try {
            if (isHttpTraceEnabled)
              console.log('🔄 Attempting to refresh token...');
            activeRequests++;
            toggleLoadingClass(true);
            const response = await axios.post(
              `${API_BASE_URL}/auth/refresh-token`,
              {
                refreshToken,
              },
            );

            if (
              response.data.success ||
              (response.data.accessToken && response.data.refreshToken)
            ) {
              const { accessToken, refreshToken: newRefreshToken } =
                response.data;
              if (isStaleAuthWrite(generationAtStart)) {
                if (isHttpTraceEnabled) {
                  console.log(
                    '⚠️ Ignoring stale token refresh after session change',
                  );
                }
                const staleErr = new Error('Stale auth session');
                processQueue(staleErr, null);
                isRefreshing = false;
                activeRequests--;
                if (activeRequests <= 0) {
                  activeRequests = 0;
                  toggleLoadingClass(false);
                }
                return Promise.reject(staleErr);
              }
              if (isHttpTraceEnabled) {
                console.log('✅ Token refreshed successfully');
              }

              localStorage.setItem(TOKEN_KEY, accessToken);
              if (newRefreshToken) {
                localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
              }

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }

              processQueue(null, accessToken);
              isRefreshing = false;
              activeRequests--;
              if (activeRequests <= 0) {
                activeRequests = 0;
                toggleLoadingClass(false);
              }

              return http(originalRequest);
            }
            // 200 but success: false — do not loop; fail queued requests
            const softFail = new Error('Token refresh returned unsuccessful');
            processQueue(softFail, null);
            isRefreshing = false;
            activeRequests--;
            if (activeRequests <= 0) {
              activeRequests = 0;
              toggleLoadingClass(false);
            }
            return Promise.reject(error);
          } catch (refreshError: unknown) {
            console.error('❌ Token refresh failed:', refreshError);
            const ax = refreshError as AxiosError<{
              retryAfter?: number;
              message?: string;
            }>;
            if (ax.response?.status === 429) {
              const retrySec = Number(ax.response.data?.retryAfter) || 60;
              refreshCooldownUntil = Date.now() + retrySec * 1000;
              console.warn(
                `⏳ Refresh rate limited; cooling down ~${retrySec}s`,
              );
            }
            processQueue(refreshError, null);
            isRefreshing = false;
            activeRequests--;
            if (activeRequests <= 0) {
              activeRequests = 0;
              toggleLoadingClass(false);
            }
            // Fall through to logout logic
          }
        }
      }

      if (isHttpTraceEnabled) {
        console.log('🔐 [401 HANDLER] Processing 401 error...');
      }
      const currentPath = window.location.pathname;
      const hasCachedUser = localStorage.getItem(USER_KEY);
      const hasToken = localStorage.getItem(TOKEN_KEY);

      // If the user is not authenticated (no token), ignore 401s with no side effects.
      if (!hasToken) {
        return Promise.reject(error);
      }

      // Check if user is on a protected route (admin or doctor dashboard)
      const isOnProtectedRoute =
        currentPath.startsWith('/admin') || currentPath.startsWith('/doctor');
      const isProfileRefresh = requestUrl.includes('/users/me');

      // Only skip logout if:
      // 1. It's a background profile refresh AND we haven't tried to refresh the token yet.
      // 2. This allows the AuthContext 'init' flow to potentially handle its own errors
      //    without the interceptor force-redirecting during the first frame.
      // BUT if we already tried to refresh (_retry: true) or if we are on a protected
      // route that is now failing, we MUST log out to avoid "Zombie Sessions".
      if (
        isProfileRefresh &&
        hasCachedUser &&
        !originalRequest._retry &&
        !isOnProtectedRoute
      ) {
        console.warn(
          '⚠️ 401 error during background refresh, keeping user logged in for now',
          {
            isProfileRefresh,
            currentPath,
          },
        );
        return Promise.reject(error);
      }
      // Final "Zombie Session" Protection:
      // If we are on a protected route OR if this was a failed retry, force logout.
      // For non-protected routes (like /profile), we only force logout if the refresh actually failed.
      const refreshFailed = originalRequest._retry;
      const shouldForceLogout =
        isOnProtectedRoute || refreshFailed || !hasToken;

      if (
        !shouldForceLogout &&
        !isOnProtectedRoute &&
        currentPath === '/signup'
      ) {
        // Special case: never logout if on signup page unless it's a critical failure
        return Promise.reject(error);
      }

      if (shouldForceLogout) {
        if (isHttpTraceEnabled) {
          console.log('🚪 Logging out due to 401 (forced)', {
            isOnProtectedRoute,
            refreshFailed,
            currentPath,
            requestUrl,
          });
        }

        // Auto logout - token is invalid or expired
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        // Clear user-related data (saved recipes, liked recipes, collections)
        localStorage.removeItem('liked_recipes');
        localStorage.removeItem('saved_recipes');
        localStorage.removeItem('recipe_collections');

        // Only clear signup draft if not actively on signup page
        if (currentPath !== '/signup') {
          try {
            sessionStorage.removeItem('signup_user_id');
            sessionStorage.removeItem('signup_form_draft');
            sessionStorage.removeItem('signup_step');
          } catch {
            /* ignore */
          }
        }

        // Only redirect if not already on login/signup page
        if (currentPath !== '/login' && currentPath !== '/signup') {
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    }
    return Promise.reject(error);
  },
);

export default http;
